import { findCheckoutProduct } from '../config/storefrontProducts';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ShoppingCart, ArrowLeft, ArrowRight, Check, ChevronDown, ShieldCheck, ReceiptText, Loader2, Mail, KeyRound, Eye, EyeOff, CircleCheck, ArrowLeftRight, Wallet } from 'lucide-react';
import { getPaymentUrl, createCryptoPayment } from '../api/payment';
import openaiLogo from '../assets/brands/openai.svg';
import claudeLogo from '../assets/brands/claude.svg';
import './OrderConfirm.css';

const DEFAULT_PAYMENT_DOMAIN = 'getpro.plus';
const DIRECT_PAY_URL_METHODS = new Set(['alipay', 'alipay-hk', 'credit-card']);

const getPaymentDomain = () => {
  const hostname = window.location.hostname?.toLowerCase() || '';
  const localDomains = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1']);

  if (!hostname || localDomains.has(hostname) || hostname.endsWith('.localhost')) {
    return DEFAULT_PAYMENT_DOMAIN;
  }

  return hostname;
};

const getRedirectUrlByMethod = (method, response) => {
  if (DIRECT_PAY_URL_METHODS.has(method)) {
    return response?.data?.payUrl || response?.payUrl || '';
  }

  if (method === 'usdt') {
    return response?.data?.invoiceUrl || response?.invoiceUrl || '';
  }

  return response?.data?.payUrl || response?.payUrl || '';
};

const isPaymentRequestSuccessful = (method, response, redirectUrl) => {
  if (!redirectUrl) {
    return false;
  }

  if (DIRECT_PAY_URL_METHODS.has(method)) {
    return true;
  }

  return (
    response?.status === '1' ||
    response?.status === 1 ||
    response?.success === true ||
    response?.code === 100
  );
};

/**
 * 订单确认页面
 */
function OrderConfirm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // 从Redux获取用户登录状态
  const { isLoggedIn, user } = useSelector((state) => state.user);

  // 从URL获取productId
  const productId = searchParams.get('productId');
  const product = findCheckoutProduct(productId) || location.state?.product;

  const [paymentMethod, setPaymentMethod] = useState('alipay');
  const [email, setEmail] = useState('');
  const [queryPassword, setQueryPassword] = useState('');
  const [showQueryPassword, setShowQueryPassword] = useState(true);
  const [queryPasswordError, setQueryPasswordError] = useState('');
  const queryPasswordRef = useRef(null);
  const emailRef = useRef(null);
  const paybarRef = useRef(null);
  const paymentPending = useRef(false);
  const [emailError, setEmailError] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('fetching');
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    const updateViewport = () => {
      setKeyboardOpen(window.innerHeight - viewport.height > 150);
    };
    viewport.addEventListener('resize', updateViewport);
    updateViewport();
    return () => viewport.removeEventListener('resize', updateViewport);
  }, []);

  // 如果用户已登录,自动填充邮箱
  useEffect(() => {
    if (isLoggedIn && user?.uid) {
      setEmail(user.uid);
    }
  }, [isLoggedIn, user]);

  if (!product) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <div className="page-card text-center mx-4">
          <ShoppingCart className="mx-auto mb-4 text-brand" size={40} />
          <h1 className="text-2xl font-bold mb-3">先选一个产品</h1>
          <p className="text-muted mb-6 text-sm">从首页选择代充值、成品号或网页共享版。</p>
          <button
            onClick={() => navigate('/')}
            className="px-10 py-4 bg-brand text-white text-lg font-semibold rounded-xl hover:bg-brand-hover transform  transition-all duration-300 shadow-panel"
          >
            返回首页
          </button>
        </div>
      </div>
    );
  }

  const isUnavailable = product.available === false;
  const totalPrice = product.price.toFixed(1);
  const totalPriceRMB = product.priceRMB;
  const paymentDomain = getPaymentDomain();
  const deliveryCopy = product.fulfillment === 'web-shared'
    ? {
      title: '付款后自动获取网页入口',
      description: '支付成功后，专属网页入口会直接显示在支付成功页面，也可随时在订单详情页查看。',
    }
    : product.fulfillment === 'account'
      ? {
        title: '付款后自动获取成品号',
        description: '支付成功后，成品账号和激活信息会直接显示在支付成功页面，也可随时在订单详情页查看。',
      }
      : {
        title: '付款后自动获取卡密和充值地址',
        description: '支付成功后，卡密和充值地址会直接显示在支付成功页面，也可随时在订单详情页查看。',
      };

  // 支付渠道映射
  const getChannelNumber = (method) => {
    const channelMap = {
      'alipay': 1,
      'alipay-hk': 2,
      'credit-card': 10
    };
    return channelMap[method] || 1;
  };

  const keepFieldVisible = (event) => {
    const input = event.currentTarget;
    window.requestAnimationFrame(() => {
      const bounds = input.getBoundingClientRect();
      const paybarTop = paybarRef.current?.getBoundingClientRect().top ?? window.innerHeight;
      if (bounds.bottom > paybarTop || bounds.top < 80) {
        input.scrollIntoView?.({ block: 'center', behavior: 'auto' });
      }
    });
  };

  const handleConfirmOrder = async (event) => {
    event.preventDefault();
    if (paymentPending.current || isUnavailable) return;
    setPaymentError('');
    const uid = isLoggedIn ? String(user?.uid || '') : email.trim();

    if (!uid || (!isLoggedIn && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(uid))) {
      setEmailError(isLoggedIn ? '账号信息未完整加载，请重新登录后再试。' : '请输入有效的联系邮箱，例如 name@example.com');
      emailRef.current?.focus();
      return;
    }

    if (!queryPassword.trim()) {
      setQueryPasswordError('请设置此单的查询密码');
      queryPasswordRef.current?.focus();
      return;
    }

    if (!/[a-zA-Z]/.test(queryPassword) || !/[0-9]/.test(queryPassword)) {
      setQueryPasswordError('查询密码必须同时包含英文字母和数字，不能使用纯数字或纯字母');
      queryPasswordRef.current?.focus();
      return;
    }

    if (!productId) {
      setPaymentError('未找到产品，请返回首页重新选择。');
      return;
    }

    paymentPending.current = true;
    setLoading(true);
    setLoadingStep('fetching');

    try {
      let response;

      if (paymentMethod === 'usdt') {
        response = await createCryptoPayment({
          uid,
          product_id: productId,
          query_password: queryPassword,
          domain: paymentDomain,
          email: email || uid
        });
      } else {
        const channel = getChannelNumber(paymentMethod);

        response = await getPaymentUrl({
          uid,
          product_id: productId,
          query_password: queryPassword,
          domain: paymentDomain,
          channel
        });
      }

      const redirectUrl = getRedirectUrlByMethod(paymentMethod, response);

      if (isPaymentRequestSuccessful(paymentMethod, response, redirectUrl)) {
        setLoadingStep('redirecting');

        // 让按钮的跳转状态先完成一次渲染，再执行页面跳转，避免原页面闪回
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            window.location.assign(redirectUrl);
          });
        });

        return;
      } else {
        setLoading(false);
        setLoadingStep('fetching');
        paymentPending.current = false;
        setPaymentError(response?.message || '暂时无法打开支付页面，请重试或更换支付方式。');
      }
    } catch (error) {
      setLoading(false);
      setLoadingStep('fetching');
      paymentPending.current = false;
      setPaymentError('支付请求失败，请重试或更换支付方式。');
    }
  };

  const paymentOptions = [
    { value: 'alipay', label: '支付宝', image: 'alipay.jpg' },
    { value: 'alipay-hk', label: '支付宝香港', image: 'alipay_hk.jpg' },
    { value: 'credit-card', label: '信用卡', image: 'credicard.jpg', detail: 'Visa / Mastercard' },
    { value: 'usdt', label: 'USDT', image: 'usdt.jpg' },
  ];
  const selectedMethod = paymentOptions.find((option) => option.value === paymentMethod);

  return (
    <div className={`checkout-page${keyboardOpen ? ' checkout-keyboard-open' : ''}`}>
      <div className="checkout-heading">
        <Link to="/" className="checkout-back"><ArrowLeft size={16} aria-hidden="true" />更换产品</Link>
        <h1>确认产品，完成购买</h1>
        <p>选好支付方式，填两项信息，就可以付款。</p>
      </div>

      <section className="checkout-product" aria-label="订单产品">
        <div className="checkout-product-info">
          <div className="checkout-logos" aria-hidden="true"><img src={openaiLogo} alt="" width="32" height="32" />{product.includesClaude && <img src={claudeLogo} alt="" width="32" height="32" />}</div>
          <div><h2>{product.name}</h2><p>1 个月 · {product.fulfillment === 'web-shared' ? '专属网页入口' : product.fulfillment === 'account' ? '新账号 + 升级卡密' : '充值你自己的账号'}</p></div>
        </div>
        <div className="checkout-product-price"><span>¥</span><strong>{totalPriceRMB}</strong></div>
        {product.accountFeeRMB > 0 && <p className="checkout-fee">订阅 ¥{product.basePriceRMB} + 账号费 ¥{product.accountFeeRMB}</p>}
      </section>

      {isUnavailable && <p className="checkout-stock-notice" role="status"><ShoppingCart size={18} aria-hidden="true" />该产品暂时缺货，请选择其他产品。</p>}

      <form id="checkout-form" onSubmit={handleConfirmOrder} noValidate aria-busy={loading}>
        <div className="checkout-form-card">
          <fieldset className="checkout-payment" disabled={loading || isUnavailable}>
            <legend><span className="checkout-step">01</span>选择支付方式</legend>
            <div className="checkout-methods">
              {paymentOptions.map((option) => (
                <label key={option.value} className={`checkout-method ${paymentMethod === option.value ? 'is-selected' : ''}`}>
                  <input type="radio" name="payment" value={option.value} checked={paymentMethod === option.value} onChange={() => { setPaymentMethod(option.value); setPaymentError(''); }} className="sr-only" />
                  <span className="checkout-method-check" aria-hidden="true">{paymentMethod === option.value && <Check size={12} strokeWidth={3} />}</span>
                  <img src={`${process.env.PUBLIC_URL}/images/payment/${option.image}`} alt="" width="30" height="30" />
                  <strong>{option.label}</strong>
                  {option.detail && <small>{option.detail}</small>}
                </label>
              ))}
            </div>
          </fieldset>

          <section className="checkout-information" aria-labelledby="checkout-information-title">
            <div className="checkout-information-heading"><h2 id="checkout-information-title"><span className="checkout-step">02</span>填写订单信息</h2><span className="checkout-brand-seal" aria-hidden="true"><span>✳</span><img src="/images/logo.jpg" alt="" width="28" height="28" /><span>+</span></span></div>
            <div className="checkout-fields">
              <div className="checkout-field">
                <label htmlFor="checkout-email">联系邮箱{isLoggedIn && <span className="checkout-field-badge"><CircleCheck size={12} aria-hidden="true" />已登录账号</span>}</label>
                <div className="checkout-input-shell"><Mail className="checkout-input-icon" size={18} aria-hidden="true" /><input ref={emailRef} onFocus={keepFieldVisible} id="checkout-email" name="email" type="email" autoComplete="email" inputMode="email" required value={email} readOnly={isLoggedIn} disabled={loading || isUnavailable} onChange={(event) => { setEmail(event.target.value); setEmailError(''); }} placeholder="name@example.com" aria-invalid={Boolean(emailError)} aria-describedby={`checkout-email-help${emailError ? ' checkout-email-error' : ''}`} /></div>
                <p id="checkout-email-help">{isLoggedIn ? '订单关联当前登录账号。' : '用于查订单、收通知，无需填写 ChatGPT 邮箱。'}</p>
                {emailError && <p className="checkout-error" id="checkout-email-error" role="alert">{emailError}</p>}
              </div>
              <div className="checkout-field">
                <label htmlFor="order-query-password">此单查询密码<span className="checkout-field-badge"><ShieldCheck size={12} aria-hidden="true" />请妥善保存</span></label>
                <div className="checkout-input-shell checkout-password"><KeyRound className="checkout-input-icon" size={18} aria-hidden="true" />
                  <input ref={queryPasswordRef} onFocus={keepFieldVisible} id="order-query-password" name="orderQueryPassword" type={showQueryPassword ? 'text' : 'password'} autoComplete="new-password" autoCapitalize="none" spellCheck={false} required disabled={loading || isUnavailable} value={queryPassword} onChange={(event) => { setQueryPassword(event.target.value); setQueryPasswordError(''); }} placeholder="设置密码，包含字母和数字" aria-invalid={Boolean(queryPasswordError)} aria-describedby={`order-query-password-help${queryPasswordError ? ' order-query-password-error' : ''}`} />
                  <button type="button" disabled={loading || isUnavailable} onClick={() => setShowQueryPassword((visible) => !visible)} aria-label={showQueryPassword ? '隐藏查询密码' : '显示查询密码'} aria-controls="order-query-password">{showQueryPassword ? <EyeOff size={15} aria-hidden="true" /> : <Eye size={15} aria-hidden="true" />}{showQueryPassword ? '隐藏' : '显示'}</button>
                </div>
                <p id="order-query-password-help">付款后用它领取交付内容，不是 ChatGPT 密码。</p>
                {queryPasswordError && <p className="checkout-error" id="order-query-password-error" role="alert">{queryPasswordError}</p>}
              </div>
            </div>
          </section>
          {paymentError && <p className="checkout-error checkout-payment-error" role="alert">{paymentError}</p>}
        </div>

        <div ref={paybarRef} className="checkout-paybar">
          <div className="checkout-paybar-inner">
            <div className="checkout-pay-total"><span><ReceiptText size={14} aria-hidden="true" />合计 · 1 个月</span><strong>¥{totalPriceRMB}</strong></div>
            <button type="submit" className="brand-purchase checkout-submit" disabled={loading || isUnavailable}>
              {isUnavailable ? '暂时缺货' : loading ? <><Loader2 size={20} className="motion-safe:animate-spin" aria-hidden="true" />{loadingStep === 'redirecting' ? '正在跳转…' : '正在准备支付…'}</> : <><Wallet size={19} aria-hidden="true" />确认支付<ArrowRight size={20} aria-hidden="true" /></>}
            </button>
            <p className="checkout-channel" aria-live="polite">{!loading && !isUnavailable && <img src={`${process.env.PUBLIC_URL}/images/payment/${selectedMethod.image}`} alt="" width="15" height="15" />}{isUnavailable ? '补货后恢复购买' : loading ? '请稍候，无需重复点击' : `通过${selectedMethod.label}支付`}</p>
            <p className="checkout-currency-note"><ArrowLeftRight size={14} aria-hidden="true" /><span>订单以美元计价，人民币金额供参考。实际支付金额可能因汇率波动略有差异，以支付页面为准。</span></p>
          </div>
        </div>
      </form>

      <div className="checkout-delivery"><ShieldCheck size={18} aria-hidden="true" /><p>{deliveryCopy.title}，也可在<Link to="/orders">历史订单</Link>查看。</p></div>
      <details className="checkout-details">
        <summary>交付与售后说明<ChevronDown size={16} aria-hidden="true" /></summary>
        <div>
          <p>{deliveryCopy.description} 如果没有看到交付内容，请联系右下角客服并提供订单号。</p>
          {String(product.productId) === '243' && <p>Plus 代充值自充值成功起享 30 天质保，掉订阅按剩余天数退款；充值失败全额退款。</p>}
          {!product.accountFeeRMB && product.fulfillment !== 'web-shared' && <p>参考外币价 ${totalPrice}，最终结算币种与金额以支付页面为准。</p>}
          <p>查询密码必须包含英文字母和数字，建议 8 位及以上。请为订单单独设置并妥善保存，勿与他人分享。</p>
          <Link to="/upgrade-guide">查看升级与使用教程 ↗</Link>
        </div>
      </details>
      <p className="checkout-invoice"><ReceiptText size={15} aria-hidden="true" />需要 Invoice？联系右下角客服免费领取。</p>
    </div>
  );
}

export default OrderConfirm;
